import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { flattenApproach } from '../../../api/marketplaceApi';
import type {
  ApproachChildTemplate,
  ApproachDefinition,
  ApproachReference,
  ApproachStatusFactorConfig,
  ProvidedContext,
  ProvidedType,
} from 'types/approach';
import type { UnifiedFilters } from 'types/filters';
import TagAtom from '../../atoms/Tag/TagAtom';
import ArcStagesMolecule from '../../molecules/marketplace/ArcStagesMolecule';
import ChildSpheresMolecule from '../../molecules/marketplace/ChildSpheresMolecule';
import OrbitScheduleMolecule from '../../molecules/marketplace/OrbitScheduleMolecule';
import SubApproachReferenceMolecule from '../../molecules/marketplace/SubApproachReferenceMolecule';
import { useSchemaMetadata } from '../../../hooks/useSchemaMetadata';
import { useContextMetadata } from '../../../hooks/useContextMetadata';
import { classifyApplicability } from '../../../utils/marketplace/classifyApplicability';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './WhatsIncludedReaderOrganism.scss';

const WI_TINT = ['tint-0', 'tint-1', 'tint-2', 'tint-3', 'tint-4'] as const;
const WI_BORDER = [
  'border-0',
  'border-1',
  'border-2',
  'border-3',
  'border-4',
] as const;

export interface WhatsIncludedReaderOrganismProps {
  /** System name of the approach to render. */
  systemName: string;
  /** Specific version to flatten; defaults to latest published. */
  version?: number;
  /**
   * Pre-resolved flattened definition. When provided, the organism skips the
   * API fetch and renders immediately. Used by the page-level mount that
   * already has the approach definition in hand.
   */
  initialDefinition?: ApproachDefinition;
  /** Recursion depth (0 = root). Drives depth tint. */
  depth?: number;
  /**
   * Set of ancestor `systemName`s on the current render path. Used as a
   * client-side cycle guard (defense-in-depth — backend `detectCycles`
   * is the primary).
   */
  visited?: ReadonlySet<string>;
  /**
   * Applicability filters from the approach version. When provided at depth 0,
   * renders an "Available for: {types}" chip in the header pills row alongside
   * the deployment-mode pill.
   */
  applicabilityFilters?: UnifiedFilters | null;
  className?: string;
}

interface BucketProps {
  icon: string;
  dataBucket: string;
  title: string;
  subtitle?: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const Bucket: React.FC<BucketProps> = ({
  icon,
  dataBucket,
  title,
  subtitle,
  count,
  defaultOpen = true,
  children,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="marketplace-wi-reader__bucket" data-bucket={dataBucket}>
      <button
        type="button"
        className="marketplace-wi-reader__bucket-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={
          open
            ? t('marketplace.approachDetail.whatsIncluded.collapseAriaLabel', {
                name: title,
              })
            : t('marketplace.approachDetail.whatsIncluded.expandAriaLabel', {
                name: title,
              })
        }
      >
        <i
          className={`pi pi-chevron-${open ? 'down' : 'right'} marketplace-wi-reader__bucket-chevron`}
          aria-hidden="true"
        />
        <span
          className="marketplace-wi-reader__bucket-icon-box"
          aria-hidden="true"
        >
          <i className={`pi ${icon}`} />
        </span>
        <span className="marketplace-wi-reader__bucket-title">{title}</span>
        {count != null && (
          <span className="marketplace-wi-reader__bucket-count">{count}</span>
        )}
        {subtitle && (
          <span className="marketplace-wi-reader__bucket-subtitle">
            {subtitle}
          </span>
        )}
      </button>
      {open && (
        <div className="marketplace-wi-reader__bucket-body">{children}</div>
      )}
    </div>
  );
};

const WhatsIncludedReaderOrganism: React.FC<WhatsIncludedReaderOrganismProps> = ({
  systemName,
  version,
  initialDefinition,
  depth = 0,
  visited,
  applicabilityFilters,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const ancestors = visited ?? new Set<string>();
  const isCycle = ancestors.has(systemName);

  const [definition, setDefinition] = useState<ApproachDefinition | null>(
    initialDefinition ?? null,
  );
  const [loading, setLoading] = useState<boolean>(
    !initialDefinition && !isCycle,
  );
  const [hasError, setHasError] = useState<boolean>(false);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    if (initialDefinition || isCycle) return;
    if (!apiClients?.schemaClient || !organizationId) return;

    let cancelled = false;
    setLoading(true);
    setHasError(false);

    flattenApproach(apiClients.schemaClient, systemName, {
      version,
    })
      .then((res) => {
        if (cancelled) return;
        setDefinition(res.definition);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    apiClients,
    organizationId,
    systemName,
    version,
    initialDefinition,
    isCycle,
    reloadCounter,
  ]);

  const nextVisited = useMemo(() => {
    const s = new Set(ancestors);
    s.add(systemName);
    return s;
  }, [ancestors, systemName]);

  const handleRetry = useCallback(() => {
    setReloadCounter((c) => c + 1);
  }, []);

  // Compute the "Available for" chip label unconditionally so the hook call
  // order stays stable across renders (Rules of Hooks). Only consumed in the
  // happy-path render below.
  const applicabilityChipLabel = useApplicabilityChipLabel(
    depth === 0 ? applicabilityFilters ?? null : null,
    t,
  );

  const tintClass = `marketplace-wi-reader--${WI_TINT[depth % WI_TINT.length]}`;
  const borderClass = `marketplace-wi-reader--${WI_BORDER[depth % WI_BORDER.length]}`;
  const rootClasses = ['marketplace-wi-reader', tintClass, borderClass, className]
    .filter(Boolean)
    .join(' ');

  if (isCycle) {
    return (
      <div
        className={`${rootClasses} marketplace-wi-reader--cyclic`}
        data-depth={depth}
        data-system-name={systemName}
        role="note"
      >
        <i
          className="pi pi-replay marketplace-wi-reader__cyclic-icon"
          aria-hidden="true"
        />
        <span className="marketplace-wi-reader__cyclic-text">
          {t('marketplace.approachDetail.whatsIncluded.cyclicReference')}
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={`${rootClasses} marketplace-wi-reader--loading`}
        data-depth={depth}
        role="status"
        aria-busy="true"
      >
        <span className="marketplace-wi-reader__loading-text">
          {t('marketplace.approachDetail.whatsIncluded.loading')}
        </span>
      </div>
    );
  }

  if (hasError || !definition) {
    return (
      <div
        className={`${rootClasses} marketplace-wi-reader--error`}
        data-depth={depth}
        data-system-name={systemName}
        role="alert"
      >
        <span className="marketplace-wi-reader__error-text">
          {depth > 0
            ? t(
                'marketplace.approachDetail.whatsIncluded.referencedNotAvailable',
              )
            : t('marketplace.approachDetail.whatsIncluded.loadError')}
        </span>
        {depth === 0 && (
          <button
            type="button"
            className="marketplace-wi-reader__retry"
            onClick={handleRetry}
          >
            {t('marketplace.approachDetail.whatsIncluded.loadErrorRetry')}
          </button>
        )}
      </div>
    );
  }

  const dependencies: ApproachReference[] = (definition.subApproaches ?? []).filter(
    (r) => r.mode === 'dependency',
  );
  const includes: ApproachReference[] = (definition.subApproaches ?? []).filter(
    (r) => r.mode === 'include',
  );
  const hasArc = !!definition.arc;
  const providedTypes: ProvidedType[] = definition.providedTypes ?? [];
  const children: ApproachChildTemplate[] = definition.children ?? [];
  const hasOrbit = !!definition.orbit;
  const providedContexts: ProvidedContext[] = definition.providedContexts ?? [];
  const factors: ApproachStatusFactorConfig[] = definition.statusFactors ?? [];

  const visibleBuckets: Array<{
    key: string;
    icon: string;
    dataBucket: string;
    title: string;
    subtitle?: string;
    count?: number;
    body: React.ReactNode;
  }> = [];

  if (dependencies.length > 0) {
    visibleBuckets.push({
      key: 'prerequisites',
      icon: 'pi-link',
      dataBucket: 'prerequisites',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.prerequisites'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.prerequisitesSubtitle'),
      count: dependencies.length,
      body: (
        <ul className="marketplace-wi-reader__list marketplace-wi-reader__list--prereq-cards">
          {dependencies.map((dep) => (
            <li
              key={dep.approachSystemName}
              className="marketplace-wi-reader__prereq-item"
            >
              {/* V2: deployment-status badge ("Already deployed / Not yet deployed") slot — needs buyer-sphere context */}
              <SubApproachReferenceMolecule
                reference={dep}
                href={`/marketplace/approaches/${encodeURIComponent(dep.approachSystemName)}`}
              />
            </li>
          ))}
        </ul>
      ),
    });
  }

  if (includes.length > 0) {
    visibleBuckets.push({
      key: 'includes',
      icon: 'pi-clone',
      dataBucket: 'includes',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.includes'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.includesSubtitle'),
      count: includes.length,
      body: (
        <ul className="marketplace-wi-reader__list">
          {includes.map((inc) => (
            <li
              key={inc.approachSystemName}
              className="marketplace-wi-reader__nested-include"
            >
              <WhatsIncludedReaderOrganism
                systemName={inc.approachSystemName}
                version={inc.approachVersion}
                depth={depth + 1}
                visited={nextVisited}
              />
            </li>
          ))}
        </ul>
      ),
    });
  }

  if (hasArc && definition.arc) {
    visibleBuckets.push({
      key: 'arc',
      icon: 'pi-sitemap',
      dataBucket: 'arc',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.arc'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.arcSubtitle'),
      body: <ArcStagesMolecule arc={definition.arc} />,
    });
  }

  if (providedTypes.length > 0) {
    visibleBuckets.push({
      key: 'sphereTypes',
      icon: 'pi-tags',
      dataBucket: 'sphereTypes',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.sphereTypes'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.sphereTypesSubtitle'),
      count: providedTypes.length,
      body: (
        <ProvidedTypesList types={providedTypes} />
      ),
    });
  }

  if (children.length > 0) {
    visibleBuckets.push({
      key: 'childSpheres',
      icon: 'pi-share-alt',
      dataBucket: 'childSpheres',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.childSpheres'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.childSpheresSubtitle'),
      count: children.length,
      body: (
        <ChildSpheresMolecule
          children={children}
          renderNestedReader={(ref) => (
            <WhatsIncludedReaderOrganism
              systemName={ref.approachSystemName}
              version={ref.approachVersion}
              depth={depth + 1}
              visited={nextVisited}
            />
          )}
        />
      ),
    });
  }

  if (hasOrbit && definition.orbit) {
    visibleBuckets.push({
      key: 'orbit',
      icon: 'pi-calendar',
      dataBucket: 'orbit',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.orbit'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.orbitSubtitle'),
      body: <OrbitScheduleMolecule orbit={definition.orbit} />,
    });
  }

  if (providedContexts.length > 0) {
    visibleBuckets.push({
      key: 'contexts',
      icon: 'pi-bookmark',
      dataBucket: 'contexts',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.contexts'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.contextsSubtitle'),
      count: providedContexts.length,
      body: <ProvidedContextsList contexts={providedContexts} />,
    });
  }

  if (factors.length > 0) {
    visibleBuckets.push({
      key: 'targetSphereConfig',
      icon: 'pi-chart-bar',
      dataBucket: 'targetSphereConfig',
      title: t('marketplace.approachDetail.whatsIncluded.buckets.targetSphereConfig'),
      subtitle: t('marketplace.approachDetail.whatsIncluded.buckets.targetSphereConfigSubtitle'),
      count: factors.length,
      body: (
        <ul className="marketplace-wi-reader__list">
          {factors.map((f) => (
            <li key={f.id} className="marketplace-wi-reader__row">
              <span className="marketplace-wi-reader__row-name">
                {getLocalizedText(f.name) || f.id}
              </span>
              <TagAtom tone="outline" label={f.factorType} />
            </li>
          ))}
        </ul>
      ),
    });
  }

  return (
    <section
      className={rootClasses}
      data-depth={depth}
      data-system-name={systemName}
    >
      {depth === 0 && (definition.deploymentMode || applicabilityChipLabel) && (
        <div className="marketplace-wi-reader__header-pills">
          {definition.deploymentMode && (
            <TagAtom
              tone="info"
              icon="pi-send"
              label={t(
                `marketplace.approachDetail.deploymentMode.${definition.deploymentMode}`,
              )}
            />
          )}
          {applicabilityChipLabel && (
            <TagAtom
              tone="info"
              icon="pi-bullseye"
              label={applicabilityChipLabel}
            />
          )}
        </div>
      )}
      {visibleBuckets.map((b) => (
        <Bucket
          key={b.key}
          icon={b.icon}
          dataBucket={b.dataBucket}
          title={b.title}
          subtitle={b.subtitle}
          count={b.count}
        >
          {b.body}
        </Bucket>
      ))}
    </section>
  );
};

// Resolves the "Available for: …" header chip label from applicability
// filters. Returns null when no filters exist or when the approach is
// applicable everywhere (the chip would add no information). Uses
// `useSchemaMetadata` to render the schema display names instead of the
// raw `center_xxx` system names.
function formatSchemaLabel(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function useApplicabilityChipLabel(
  filters: UnifiedFilters | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  const classified = useMemo(() => classifyApplicability(filters), [filters]);
  const schemaSystemNames = useMemo(
    () => classified.sphereTypes ?? [],
    [classified.sphereTypes],
  );
  const { resolved: schemaMetadata } = useSchemaMetadata(schemaSystemNames);

  return useMemo(() => {
    if (!filters) return null;
    if (classified.mode === 'all') return null;
    if (classified.mode === 'include' && classified.sphereTypes.length > 0) {
      const labels = classified.sphereTypes
        .map(
          (name) =>
            schemaMetadata.get(name)?.displayName ?? formatSchemaLabel(name),
        )
        .join(', ');
      return t(
        'marketplace.approachDetail.whatsIncluded.availableForTypes',
        { types: labels },
      );
    }
    if (classified.mode === 'family' && (classified.families?.length ?? 0) > 0) {
      const families = (classified.families ?? [])
        .map(formatSchemaLabel)
        .join(', ');
      return t(
        'marketplace.approachDetail.whatsIncluded.availableForFamily',
        { family: families },
      );
    }
    return null;
  }, [filters, classified, schemaMetadata, t]);
}

// Shared list-card primitive used by ProvidedTypesList and
// ProvidedContextsList. Icon-in-box on the left, name + description on the
// right. Both buckets render as a single column of these cards so the
// "What's included" tab has one consistent card pattern.
interface ListCardProps {
  icon: string;
  label: string;
  description?: string;
  badge?: React.ReactNode;
}

const ListCard: React.FC<ListCardProps> = ({
  icon,
  label,
  description,
  badge,
}) => (
  <div className="marketplace-wi-reader__list-card">
    <span
      className="marketplace-wi-reader__list-card-icon"
      aria-hidden="true"
    >
      <i className={`pi ${icon}`} />
    </span>
    <div className="marketplace-wi-reader__list-card-text">
      <span className="marketplace-wi-reader__list-card-label">{label}</span>
      {description && (
        <span className="marketplace-wi-reader__list-card-description">
          {description}
        </span>
      )}
    </div>
    {badge && (
      <span className="marketplace-wi-reader__list-card-badge">{badge}</span>
    )}
  </div>
);

// Internal helper: render the Contexts Provided bucket body using the
// context-metadata hook so each row shows the context's friendly localized
// name and description, instead of the raw `xxx_yyy_zzz` system_name.
const ProvidedContextsList: React.FC<{ contexts: ProvidedContext[] }> = ({
  contexts,
}) => {
  const systemNames = useMemo(
    () => contexts.map((c) => c.contextSystemName),
    [contexts],
  );
  const { resolved } = useContextMetadata(systemNames);

  return (
    <div className="marketplace-wi-reader__list-cards marketplace-wi-reader__list-cards--grid">
      {contexts.map((c) => {
        const meta = resolved.get(c.contextSystemName);
        const label = meta?.displayName ?? c.contextSystemName;
        return (
          <ListCard
            key={c.contextSystemName}
            icon="pi-bookmark"
            label={label}
            description={meta?.description ?? undefined}
          />
        );
      })}
    </div>
  );
};

// Internal helper: render the Sphere Types Provided bucket using the shared
// ListCard primitive so it matches the Contexts treatment visually.
const ProvidedTypesList: React.FC<{ types: ProvidedType[] }> = ({ types }) => {
  const { t } = useTranslation();
  const systemNames = useMemo(
    () => types.map((pt) => pt.schemaSystemName),
    [types],
  );
  const { resolved } = useSchemaMetadata(systemNames);

  return (
    <div className="marketplace-wi-reader__list-cards marketplace-wi-reader__list-cards--grid">
      {types.map((pt) => {
        const meta = resolved.get(pt.schemaSystemName);
        const label = meta?.displayName ?? pt.schemaSystemName;
        return (
          <ListCard
            key={pt.schemaSystemName}
            // Schemas don't ship a canonical PrimeIcon; fall back to a generic
            // shape glyph. The avatar URL from metadata isn't a `pi-*` class
            // so we can't pass it directly to ListCard's icon slot — kept as
            // a follow-up to render real schema avatars here.
            icon="pi-box"
            label={label}
            description={meta?.description ?? undefined}
            badge={
              pt.isForked ? (
                <TagAtom
                  tone="warn"
                  label={t('marketplace.approachDetail.providedTypes.forked')}
                />
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
};

WhatsIncludedReaderOrganism.displayName = 'WhatsIncludedReaderOrganism';

export default WhatsIncludedReaderOrganism;
